import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';

@Component({
    selector: 'app-signature-pad',
    templateUrl: './signature-pad.component.html',
    styleUrls: ['./signature-pad.component.css'],
    standalone: false
})
export class SignaturePadComponent implements AfterViewInit, OnChanges {
  @Input() value = '';
  @Input() disabled = false;
  @Input() label = '';
  @Input() canvasWidth = 400;
  @Input() canvasHeight = 150;
  @Input() allowAttach = false;
  /** Franja superior que no se dibuja y viaja en blanco en el PNG. 0.25 = 25 %. */
  @Input() topReserveRatio = 0;

  /** Relación del área visible. El PNG sigue incluyendo la franja superior en blanco. */
  get signableAspect(): string | null {
    if (this.topReserveRatio <= 0) {
      return null;
    }
    const ratio = Math.min(Math.max(this.topReserveRatio, 0), 0.9);
    return `${this.canvasWidth} / ${this.canvasHeight * (1 - ratio)}`;
  }

  @Output() valueChange = new EventEmitter<string>();
  @Output() touched = new EventEmitter<void>();

  @ViewChild('signatureCanvas') signatureCanvas!: ElementRef<HTMLCanvasElement>;
  @ViewChild('fileInput') fileInput?: ElementRef<HTMLInputElement>;

  private context!: CanvasRenderingContext2D;
  private drawing = false;
  private strokeOpen = false;

  ngAfterViewInit(): void {
    this.initCanvas();
    this.setupTouchEvents();
    if (this.value) {
      this.renderDataUrl(this.value);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['value'] && !changes['value'].firstChange && this.context) {
      if (!this.value) {
        this.clearInternal(false);
      } else {
        this.renderDataUrl(this.value);
      }
    }
    if (changes['disabled'] && this.context) {
      this.applyDisabledState();
    }
  }

  isBlank(): boolean {
    if (!this.context || !this.signatureCanvas) {
      return true;
    }
    const canvas = this.signatureCanvas.nativeElement;
    const pixelBuffer = new Uint32Array(
      this.context.getImageData(0, 0, canvas.width, canvas.height).data.buffer
    );
    return !pixelBuffer.some((color) => color !== 0);
  }

  clear(): void {
    this.clearInternal(true);
  }

  toDataUrl(): string {
    if (!this.signatureCanvas || this.isBlank()) {
      return '';
    }
    return this.signatureCanvas.nativeElement.toDataURL('image/png');
  }

  openFileSelector(): void {
    if (this.disabled || !this.allowAttach) {
      return;
    }
    this.fileInput?.nativeElement.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file || this.disabled) {
      return;
    }
    const reader = new FileReader();
    reader.onload = (e: ProgressEvent<FileReader>) => {
      const dataUrl = e.target?.result as string;
      if (dataUrl) {
        this.renderDataUrl(dataUrl);
        this.emitValue(dataUrl);
        this.touched.emit();
      }
    };
    reader.readAsDataURL(file);
    input.value = '';
  }

  startDrawing(event: MouseEvent): void {
    if (this.disabled) {
      return;
    }
    this.drawing = true;
    this.strokeOpen = false;
    const point = this.canvasPoint(event.clientX, event.clientY);
    this.strokeTo(point.x, point.y);
  }

  draw(event: MouseEvent): void {
    if (!this.drawing || this.disabled) {
      return;
    }
    const point = this.canvasPoint(event.clientX, event.clientY);
    this.strokeTo(point.x, point.y);
  }

  stopDrawing(): void {
    if (!this.drawing) {
      return;
    }
    this.drawing = false;
    this.strokeOpen = false;
    this.syncValueFromCanvas();
  }

  private initCanvas(): void {
    const canvas = this.signatureCanvas.nativeElement;
    canvas.width = this.canvasWidth;
    canvas.height = this.canvasHeight;
    this.context = canvas.getContext('2d', { willReadFrequently: true }) as CanvasRenderingContext2D;
    this.context.lineWidth = 2;
    this.context.lineCap = 'round';
    this.context.strokeStyle = '#000';
    this.applyDisabledState();
  }

  private applyDisabledState(): void {
    if (!this.signatureCanvas) {
      return;
    }
    this.signatureCanvas.nativeElement.style.opacity = this.disabled ? '0.6' : '1';
    this.signatureCanvas.nativeElement.style.pointerEvents = this.disabled ? 'none' : 'auto';
  }

  private setupTouchEvents(): void {
    const canvas = this.signatureCanvas.nativeElement;
    canvas.ontouchstart = (event: TouchEvent) => {
      if (this.disabled) {
        return;
      }
      event.preventDefault();
      this.drawing = true;
      this.strokeOpen = false;
      const point = this.touchPoint(event);
      this.strokeTo(point.x, point.y);
    };
    canvas.ontouchmove = (event: TouchEvent) => {
      if (this.disabled) {
        return;
      }
      event.preventDefault();
      if (!this.drawing) {
        return;
      }
      const point = this.touchPoint(event);
      this.strokeTo(point.x, point.y);
    };
    canvas.ontouchend = (event: TouchEvent) => {
      if (this.disabled) {
        return;
      }
      event.preventDefault();
      this.drawing = false;
      this.strokeOpen = false;
      this.syncValueFromCanvas();
    };
  }

  private syncValueFromCanvas(): void {
    if (this.isBlank()) {
      this.emitValue('');
    } else {
      this.emitValue(this.toDataUrl());
    }
    this.touched.emit();
  }

  private emitValue(next: string): void {
    this.value = next;
    this.valueChange.emit(next);
  }

  private clearInternal(emit: boolean): void {
    if (!this.context || !this.signatureCanvas) {
      return;
    }
    const canvas = this.signatureCanvas.nativeElement;
    this.context.clearRect(0, 0, canvas.width, canvas.height);
    if (emit) {
      this.emitValue('');
      this.touched.emit();
    }
  }

  private renderDataUrl(dataUrl: string): void {
    if (!this.context || !dataUrl) {
      return;
    }
    const canvas = this.signatureCanvas.nativeElement;
    const image = new Image();
    image.onload = () => {
      this.context.clearRect(0, 0, canvas.width, canvas.height);
      const ratio = Math.min(canvas.width / image.width, canvas.height / image.height);
      const w = image.width * ratio;
      const h = image.height * ratio;
      const x = (canvas.width - w) / 2;
      const y = (canvas.height - h) / 2;
      this.context.drawImage(image, x, y, w, h);
    };
    image.src = dataUrl;
  }

  private reserveMinY(): number {
    const ratio = Math.min(Math.max(this.topReserveRatio, 0), 0.9);
    return this.signatureCanvas.nativeElement.height * ratio;
  }

  /** No pinta por encima de la franja reservada. El PNG conserva el alto completo. */
  private strokeTo(x: number, y: number): void {
    const minY = this.reserveMinY();
    if (y < minY) {
      if (!this.strokeOpen) {
        return;
      }
      this.context.lineTo(x, minY);
      this.context.stroke();
      this.strokeOpen = false;
      return;
    }
    if (!this.strokeOpen) {
      this.context.beginPath();
      this.context.moveTo(x, y);
      this.strokeOpen = true;
      return;
    }
    this.context.lineTo(x, y);
    this.context.stroke();
  }

  private touchPoint(event: TouchEvent): { x: number; y: number } {
    const touch = event.touches[0] || event.changedTouches[0];
    return this.canvasPoint(touch.clientX, touch.clientY);
  }

  /** CSS pixels → bitmap. El canvas se muestra más chico que su resolución interna. */
  private canvasPoint(clientX: number, clientY: number): { x: number; y: number } {
    const canvas = this.signatureCanvas.nativeElement;
    const rect = canvas.getBoundingClientRect();
    const scaleX = rect.width > 0 ? canvas.width / rect.width : 1;
    const scaleY = rect.height > 0 ? canvas.height / rect.height : 1;
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  }
}
